/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/organizationManagement.ts
 * PURPOSE: Organization Management API service for ADR-028 8-Layer Hierarchy
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 7: Performance SLAs (<50ms p95)
 *
 * DATE: 2026-01-19
 * DESIGN: docs/02-design/04-ui-ux-design/ORGANIZATION-MANAGEMENT-UI-DESIGN.md
 * APPROVED: Sprint 27 - Pilot Onboarding
 * COMPLIANCE: SDLC 6.3.1 + ADR-028 Organization Entity Schema
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management - Organization Hierarchy
 * - Impact: API integration for 8-Layer hierarchy CRUD operations
 * - Stakeholders: Frontend Team, Backend Team
 *
 * @technical-architecture
 * - Layer: Data Access Layer
 * - Pattern: Service Pattern, Async API calls
 * - Dependencies: apiRequest from config, OrganizationEntity types
 *
 * @cross-references
 * - ADR: docs/02-design/18-adrs/ADR-028-organization-entity-schema-standardization.md
 * - API: backend/app/api/v1/organization.py
 * - Types: frontend/src/types/organization.ts
 */

import { apiRequest } from './config';
import {
  OrganizationEntity,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationFilters,
  OrganizationType,
} from '@/types/organization';
import { AUTH_STORAGE_KEYS } from '@/store/auth';

/**
 * API Response wrapper for list endpoints
 */
export interface OrganizationListResponse {
  items: OrganizationEntity[];
  total: number;
  skip: number;
  limit: number;
}

/**
 * Organization Statistics
 */
export interface OrganizationStats {
  total_organizations: number;
  by_type: Record<OrganizationType, number>;
  by_status: {
    active: number;
    inactive: number;
    pending: number;
  };
  total_employees: number;
}

/**
 * Get auth token from localStorage
 * Per ADR-frontend-auth-state-management.md: Token stored as AUTH_STORAGE_KEYS.ACCESS_TOKEN
 */
const getAuthToken = (): string | null => {
  return localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * Organization Management API Service
 * Implements ADR-028 Organization Entity Schema
 */
export const organizationManagementApi = {
  /**
   * List all organizations with optional filters
   * GET /api/v1/organizations/
   */
  async listOrganizations(filters?: OrganizationFilters): Promise<OrganizationEntity[]> {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const params = new URLSearchParams();
    if (filters?.organization_type) {
      params.append('organization_type', filters.organization_type);
    }
    if (filters?.level !== undefined) {
      params.append('level', filters.level.toString());
    }
    if (filters?.is_active !== undefined) {
      params.append('is_active', filters.is_active.toString());
    }
    if (filters?.parent_id) {
      params.append('parent_id', filters.parent_id);
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }
    if (filters?.skip !== undefined) {
      params.append('skip', filters.skip.toString());
    }
    if (filters?.limit !== undefined) {
      params.append('limit', filters.limit.toString());
    }

    const queryString = params.toString();
    // IMPORTANT: Trailing slash required for FastAPI endpoint
    const endpoint = `/organizations/${queryString ? `?${queryString}` : ''}`;

    return apiRequest<OrganizationEntity[]>(endpoint, {
      method: 'GET',
      token,
    });
  },

  /**
   * Get single organization by ID
   * GET /api/v1/organizations/{org_id}
   */
  async getOrganization(orgId: string): Promise<OrganizationEntity> {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    return apiRequest<OrganizationEntity>(`/organizations/${orgId}`, {
      method: 'GET',
      token,
    });
  },

  /**
   * Create new organization
   * POST /api/v1/organizations/
   */
  async createOrganization(data: OrganizationCreate): Promise<OrganizationEntity> {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    return apiRequest<OrganizationEntity>('/organizations/', {
      method: 'POST',
      body: JSON.stringify(data),
      token,
    });
  },

  /**
   * Update existing organization
   * PUT /api/v1/organizations/{org_id}
   */
  async updateOrganization(orgId: string, data: OrganizationUpdate): Promise<OrganizationEntity> {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    return apiRequest<OrganizationEntity>(`/organizations/${orgId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      token,
    });
  },

  /**
   * Delete organization — soft delete via DELETE /organizations/{id}
   * Backend returns 204 No Content on success.
   */
  async deleteOrganization(orgId: string): Promise<void> {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    await apiRequest<void>(`/organizations/${orgId}`, {
      method: 'DELETE',
      token,
    });
  },

  /**
   * Get organization statistics
   * GET /api/v1/organizations/{org_id}/stats
   */
  async getOrganizationStats(orgId: string): Promise<OrganizationStats> {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    return apiRequest<OrganizationStats>(`/organizations/${orgId}/stats`, {
      method: 'GET',
      token,
    });
  },

  /**
   * Get children of a parent organization
   * Used for tree view
   */
  async getChildren(parentId: string): Promise<OrganizationEntity[]> {
    return this.listOrganizations({ parent_id: parentId });
  },

  /**
   * Get organizations by type
   */
  async getByType(type: OrganizationType): Promise<OrganizationEntity[]> {
    return this.listOrganizations({ organization_type: type });
  },

  /**
   * Search organizations by name or code
   */
  async searchOrganizations(query: string): Promise<OrganizationEntity[]> {
    return this.listOrganizations({ search: query });
  },

  /**
   * Validate organization code is unique
   */
  async validateCode(code: string): Promise<boolean> {
    try {
      const organizations = await this.listOrganizations({ search: code });
      return !organizations.some(org => org.code === code);
    } catch {
      return false;
    }
  },

  /**
   * Build hierarchy tree from flat list
   */
  buildHierarchyTree(organizations: OrganizationEntity[]): OrganizationEntity[] {
    const orgMap = new Map<string, OrganizationEntity & { children?: OrganizationEntity[] }>();
    const roots: (OrganizationEntity & { children?: OrganizationEntity[] })[] = [];

    // First pass: create map
    organizations.forEach(org => {
      orgMap.set(org.id, { ...org, children: [] });
    });

    // Second pass: build tree
    organizations.forEach(org => {
      const node = orgMap.get(org.id)!;
      if (org.parent_id && orgMap.has(org.parent_id)) {
        const parent = orgMap.get(org.parent_id)!;
        parent.children = parent.children || [];
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    });

    // Sort by level and name (per ADR-028: field is 'level', NOT 'hierarchy_level')
    const sortNodes = (nodes: (OrganizationEntity & { children?: OrganizationEntity[] })[]) => {
      nodes.sort((a, b) => (a.level ?? 0) - (b.level ?? 0) || a.name.localeCompare(b.name));
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          sortNodes(node.children as (OrganizationEntity & { children?: OrganizationEntity[] })[]);
        }
      });
    };

    sortNodes(roots);
    return roots;
  },
};

export default organizationManagementApi;
